// 3b (B) — Central Document Center.
// Route: /admin/documents. Owner + Administrator only.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";

const CATEGORIES = ["", "id", "w9", "insurance_gl", "insurance_wc", "insurance_auto",
  "license", "resume_cv", "certifications", "photo_progress", "scan_form",
  "paste_note", "import_csv", "other"];
const STATES = ["", "ready_for_review", "approved", "rejected",
  "more_information_requested", "superseded", "archived"];
const RECORD_TYPES = ["", "access_request", "credential", "compliance_record",
  "estimate", "proposal", "contract", "deposit", "lead", "opportunity",
  "account", "property", "visit", "approval_request", "owner_inbox_item",
  "notification", "inbound_email", "document", "help_feedback", "demo"];

export default function DocumentCenter() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [reviewStatus, setReviewStatus] = useState("");
  const [recordType, setRecordType] = useState("");
  const [uploadFrom, setUploadFrom] = useState("");
  const [uploadTo, setUploadTo] = useState("");
  const [confidentiality, setConfidentiality] = useState("");
  const [page, setPage] = useState(0);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, skip: page * limit };
      if (q) params.q = q;
      if (category) params.category = category;
      if (reviewStatus) params.review_status = reviewStatus;
      if (recordType) params.record_type = recordType;
      if (uploadFrom) params.upload_from = uploadFrom;
      if (uploadTo) params.upload_to = uploadTo;
      if (confidentiality) params.confidentiality = confidentiality;
      const r = await api.get("/attachments", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error("Failed to load documents"); }
    finally { setLoading(false); }
  }, [q, category, reviewStatus, recordType, uploadFrom, uploadTo, confidentiality, page]);
  useEffect(() => { load(); }, [load]);

  const transition = async (id, newState, reason) => {
    try {
      await api.post(`/attachments/${id}/state`, { new_state: newState, reason });
      toast.success(`→ ${newState}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail?.code || "state_transition_failed"); }
  };

  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <AdminAppShell current="/admin/documents">
      <div data-testid="document-center-page" style={{ padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Central Document Center</h1>
            <div style={{ color: "#475569", fontSize: 13, marginTop: 4 }}>
              Every attachment across ConstructionOS — search, filter, and drill back to its authoritative record.
            </div>
          </div>
          <span data-testid="doc-center-count" style={{ fontSize: 12, color: "#64748b" }}>
            {total} {total === 1 ? "attachment" : "attachments"}
          </span>
        </div>

        <Card style={{ marginTop: 16 }}>
          <CardContent style={{ padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 10 }}>
              <label style={label}>Search
                <Input data-testid="doc-center-search" value={q}
                        onChange={(e) => { setPage(0); setQ(e.target.value); }}
                        placeholder="file name, category, label, record type…" />
              </label>
              <label style={label}>Category
                <select data-testid="doc-center-category" value={category} style={sel}
                         onChange={(e) => { setPage(0); setCategory(e.target.value); }}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c || "(any)"}</option>)}
                </select>
              </label>
              <label style={label}>Review status
                <select data-testid="doc-center-state" value={reviewStatus} style={sel}
                         onChange={(e) => { setPage(0); setReviewStatus(e.target.value); }}>
                  {STATES.map(s => <option key={s} value={s}>{s || "(any active)"}</option>)}
                </select>
              </label>
              <label style={label}>Record type
                <select data-testid="doc-center-record-type" value={recordType} style={sel}
                         onChange={(e) => { setPage(0); setRecordType(e.target.value); }}>
                  {RECORD_TYPES.map(rt => <option key={rt} value={rt}>{rt || "(any)"}</option>)}
                </select>
              </label>
              <label style={label}>Uploaded from
                <Input data-testid="doc-center-upload-from" type="date" value={uploadFrom}
                        onChange={(e) => { setPage(0); setUploadFrom(e.target.value); }} />
              </label>
              <label style={label}>Uploaded to
                <Input data-testid="doc-center-upload-to" type="date" value={uploadTo}
                        onChange={(e) => { setPage(0); setUploadTo(e.target.value); }} />
              </label>
              <label style={label}>Confidentiality
                <select data-testid="doc-center-confidentiality" value={confidentiality} style={sel}
                         onChange={(e) => { setPage(0); setConfidentiality(e.target.value); }}>
                  <option value="">(any)</option>
                  <option value="public">public</option>
                  <option value="internal">internal</option>
                  <option value="confidential">confidential</option>
                  <option value="restricted">restricted</option>
                </select>
              </label>
            </div>
          </CardContent>
        </Card>

        <Card style={{ marginTop: 16 }}>
          <CardContent style={{ padding: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                  <th style={h}>File</th><th style={h}>Category</th>
                  <th style={h}>Record</th><th style={h}>Status</th>
                  <th style={h}>Size</th><th style={h}>Uploaded</th>
                  <th style={h}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} style={{ padding: 16, color: "#64748b" }}>Loading…</td></tr>}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 20, color: "#64748b" }} data-testid="doc-center-empty">
                    No documents match the current filters.
                  </td></tr>
                )}
                {items.map((d) => (
                  <tr key={d.id} data-testid={`doc-row-${d.id}`} style={{ borderTop: "1px solid #e5e7eb" }}>
                    <td style={c}>
                      <div style={{ fontWeight: 600 }}>{d.file_name}</div>
                      <div style={{ fontSize: 10, color: "#94a3b8" }}>{d.file_type} · v{d.version} · {d.confidentiality}</div>
                    </td>
                    <td style={c}>{d.category}</td>
                    <td style={c}>
                      <div style={{ fontSize: 11 }}>{d.record_type}</div>
                      <div style={{ fontSize: 10, color: "#64748b" }}>{d.record_id.slice(0, 12)}</div>
                    </td>
                    <td style={c}>
                      <span style={statePill(d.review_status)}>{d.review_status}</span>
                    </td>
                    <td style={c}>{(d.file_size / 1024).toFixed(1)} KB</td>
                    <td style={c}>{new Date(d.upload_at).toLocaleDateString()}</td>
                    <td style={c}>
                      <a data-testid={`doc-preview-${d.id}`}
                          href={`${process.env.REACT_APP_BACKEND_URL}${d.preview_url}`}
                          target="_blank" rel="noreferrer"
                          style={{ fontSize: 11, color: "#12233F", textDecoration: "underline" }}>Preview</a>
                      {d.review_status === "ready_for_review" && (
                        <>
                          <button data-testid={`doc-approve-${d.id}`}
                                   onClick={() => transition(d.id, "approved", null)}
                                   style={rowBtn("#22c55e")}>Approve</button>
                          <button data-testid={`doc-reject-${d.id}`}
                                   onClick={() => {
                                     const r = window.prompt("Reject reason (required):");
                                     if (r) transition(d.id, "rejected", r);
                                   }}
                                   style={rowBtn("#dc2626")}>Reject</button>
                          <button data-testid={`doc-more-info-${d.id}`}
                                   onClick={() => {
                                     const r = window.prompt("What information is missing?");
                                     if (r) transition(d.id, "more_information_requested", r);
                                   }}
                                   style={rowBtn("#eab308")}>More info</button>
                        </>
                      )}
                      {d.review_status !== "archived" && d.review_status !== "superseded" && (
                        <button data-testid={`doc-archive-${d.id}`}
                                 onClick={() => transition(d.id, "archived",
                                                            window.prompt("Archive reason (optional):") || null)}
                                 style={rowBtn("#64748b")}>Archive</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button data-testid="doc-center-prev" size="sm" variant="secondary" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>← Previous</Button>
          <span style={{ fontSize: 12, color: "#64748b" }}>Page {page + 1} of {pages}</span>
          <Button data-testid="doc-center-next" size="sm" variant="secondary" disabled={page + 1 >= pages} onClick={() => setPage(p => p + 1)}>Next →</Button>
        </div>
      </div>
    </AdminAppShell>
  );
}

const label = { display: "grid", gap: 4, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#475569" };
const sel = { padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 4, fontSize: 13, background: "white" };
const h = { padding: 10, fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: 1 };
const c = { padding: 10, fontSize: 13, verticalAlign: "top" };
const stateColor = {
  ready_for_review: "#f59e0b", approved: "#22c55e", rejected: "#dc2626",
  more_information_requested: "#eab308", superseded: "#94a3b8", archived: "#64748b",
  uploading: "#0ea5e9", security_scanning: "#a855f7",
};
function statePill(s) {
  const col = stateColor[s] || "#64748b";
  return { display: "inline-block", padding: "2px 8px", borderRadius: 9999,
           background: col + "22", color: col, border: `1px solid ${col}55`,
           fontSize: 10, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 };
}
function rowBtn(color) {
  return { marginLeft: 6, padding: "2px 6px", fontSize: 10, borderRadius: 3,
           border: `1px solid ${color}55`, background: color + "12", color,
           cursor: "pointer", textTransform: "uppercase", letterSpacing: 0.3 };
}
